<template>
  <div v-if="!tour" class="tl-page">
    <div class="tl-loading">
      <div class="tl-spinner"></div>
      <span>Loading calculation data...</span>
    </div>
  </div>

  <div v-else class="tl-page">
    <!-- Margin, Compticket, Factor, and Tiers  -->
    <div class="tl-section-header tl-section-header--blue">
      <h2 class="tl-section-title">Settings and Tiers</h2>
      <div class="tl-section-actions">
        <button class="tl-btn tl-btn--ghost tl-btn--sm" v-b-tooltip.hover title="Add Tiers" @click="addTier">+</button>
        <button class="tl-btn tl-btn--primary tl-btn--sm" v-b-tooltip.hover title="Save Tiers And Submitted Price"
          @click="saveSettings">Save</button>
        <button class="tl-btn tl-btn--ghost tl-btn--sm" v-b-tooltip.hover title="Clear All Tiers"
          @click="clearTiers">Clear</button>
        <button class="tl-btn tl-btn--ghost tl-btn--sm" v-b-tooltip.hover title="Recalculate|Refresh"
          @click="updateTour">Update</button>
      </div>
    </div>

    <div class="tl-info-card">
      <div class="tl-info-grid">
        <!-- Compticket Input -->
        <div class="tl-form-group">
          <label class="tl-label" for="compticket">Compticket</label>
          <input type="number" v-model.number="compticket" class="tl-input tl-input--full" id="compticket" step="1" />
        </div>
        <!-- Margin Input -->
        <div class="tl-form-group">
          <label class="tl-label" for="margin">Margin</label>
          <input type="number" v-model.number="margin" class="tl-input tl-input--full" id="margin" step="1" />
        </div>
        <!-- Margin Child Input -->
        <div class="tl-form-group">
          <label class="tl-label" for="marginChild">Margin Child</label>
          <input type="number" v-model.number="marginChild" class="tl-input tl-input--full" id="marginChild" step="1" />
        </div>
        <!-- Margin EmptySeats Input -->
        <div class="tl-form-group">
          <label class="tl-label" for="marginEmpty">Margin Empty</label>
          <input type="number" v-model.number="marginEmpty" class="tl-input tl-input--full" id="marginEmpty" step="1" />
        </div>
        <!-- Factor Input -->
        <div class="tl-form-group">
          <label class="tl-label" for="factor">Calculation Tier</label>
          <input type="number" v-model.number="factor" class="tl-input tl-input--full" id="factor" step="1" />
        </div>
      </div>

      <!-- Tiers Input Fields -->
      <div class="mt-4 pt-4 border-top">
        <label class="tl-label mb-3 d-block">Pricing Tiers</label>
        <div class="tl-tiers-list">
          <div v-for="(tier, index) in tiers" :key="index" class="tl-tier-pill">
            <span class="tl-tier-index">Tier {{ index + 1 }}</span>
            <input type="number" v-model.number="tier.value" :id="'tier-' + index" class="tl-tier-input" step="1"
              placeholder="Value" />
            <button class="tl-tier-remove" v-b-tooltip.hover title="Remove Current Tier"
              @click="removeTier(index)">×</button>
          </div>
        </div>
        <div v-if="tiers.length === 0" class="tl-empty-state tl-empty-state--sm mt-2">
          No tiers specified. Click + to add.
        </div>
      </div>
    </div>

    <!-- Buying Prices Table -->
    <div class="tl-section-header tl-section-header--green">
      <h2 class="tl-section-title">Buying Calculation</h2>
    </div>

    <div class="tl-table-card">
      <div class="tl-table-scroll">
        <table v-if="tiers.length > 0" class="tl-table">
          <thead>
            <tr>
              <th class="tl-th tl-th--name">Cost Metric</th>
              <th v-for="(tier, index) in tiers" :key="'tier-header-' + index" class="tl-th">{{ tier.value }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Cost per Adult</td>
              <td v-for="(tier, index) in tiers" :key="'cost-adult-calculated-' + index" class="tl-td">
                <span class="tl-price-highlight">{{ formatPrice(calculateCost(tier, 'adult'), city) }}</span>
              </td>
            </tr>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Cost per Child</td>
              <td v-for="(tier, index) in tiers" :key="'cost-child-calculated-' + index" class="tl-td">
                {{ formatPrice(calculateCost(tier, 'child'), city) }}
              </td>
            </tr>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Cost per Empty Seat</td>
              <td v-for="(tier, index) in tiers" :key="'cost-empty-seat-calculated-' + index" class="tl-td">
                {{ formatPrice(calculateEmptySeatCost(tier), city) }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="tl-empty-state">
          No tiers available for calculation.
        </div>
      </div>
    </div>

    <!-- Selling Prices Table -->
    <div class="tl-section-header tl-section-header--orange">
      <h2 class="tl-section-title">
        Selling Price Calculation Based on Calculation Tier = {{ factor }}
        <span v-if="tiers.length > 0" class="tl-badge tl-badge--margin ml-2">
          Margin (1st Tier): {{ formatNumber(calculateMargin(userInputSellingPriceAdult[0], calculateCost(tiers[0],
            'adult'))) }}%
        </span>
        <span v-if="tiers.length > 1" class="tl-badge tl-badge--margin ml-1">
          Margin (2nd Tier): {{ formatNumber(calculateMargin(userInputSellingPriceAdult[1], calculateCost(tiers[1],
            'adult'))) }}%
        </span>
        <span v-if="tiers.length > 2" class="tl-badge tl-badge--margin ml-1">
          Margin (3rd Tier): {{ formatNumber(calculateMargin(userInputSellingPriceAdult[2], calculateCost(tiers[2],
            'adult'))) }}%
        </span>
      </h2>
    </div>

    <div class="tl-table-card">
      <div class="tl-table-scroll">
        <table v-if="tiers.length > 0" class="tl-table">
          <thead>
            <tr>
              <th class="tl-th tl-th--name">Price Point</th>
              <th v-for="(tier, index) in tiers" :key="'sell-tier-header-' + index" class="tl-th">{{ tier.value }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- Adult Prices -->
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Suggested Adult</td>
              <td v-for="(tier, index) in tiers" :key="'selling-adult-calculated-' + index"
                class="tl-td tl-td--suggested">
                {{ formatPrice(calculateSellingPriceForTier(tier, 'adult', index)?.adult, city) || 'N/A' }}
              </td>
            </tr>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Submitted Adult</td>
              <td v-for="(tier, index) in tiers" :key="'selling-adult-input-' + index" class="tl-td">
                <input type="number" v-model.number="userInputSellingPriceAdult[index]"
                  class="tl-input tl-input--sm tl-input--centered"
                  :placeholder="formatPrice(calculateSellingPriceForTier(tier, 'adult', index)?.adult, city) || 'N/A'"
                  step="1" />
              </td>
            </tr>
            <!-- Child Prices -->
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Suggested Child</td>
              <td v-for="(tier, index) in tiers" :key="'selling-child-calculated-' + index"
                class="tl-td tl-td--suggested">
                {{ formatPrice(calculateSellingPriceForTier(tier, 'child', index)?.child, city) || 'N/A' }}
              </td>
            </tr>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Submitted Child</td>
              <td v-for="(tier, index) in tiers" :key="'selling-child-input-' + index" class="tl-td">
                <input type="number" v-model.number="userInputSellingPriceChild[index]"
                  class="tl-input tl-input--sm tl-input--centered"
                  :placeholder="formatPrice(calculateSellingPriceForTier(tier, 'child', index)?.child, city) || 'N/A'"
                  step="1" />
              </td>
            </tr>
            <!-- Empty Seat Prices -->
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Suggested Empty Seat</td>
              <td v-for="(tier, index) in tiers" :key="'selling-empty-seat-calculated-' + index"
                class="tl-td tl-td--suggested">
                {{ formatPrice(calculateSellingPriceForEmptySeatsWithDifference(tier), city) }}
              </td>
            </tr>
            <tr class="tl-row">
              <td class="tl-td tl-td--name">Submitted Empty Seat</td>
              <td v-for="(tier, index) in tiers" :key="'selling-empty-seat-input-' + index" class="tl-td">
                <input type="number" v-model.number="userInputSellingPriceEmptySeat[index]"
                  class="tl-input tl-input--sm tl-input--centered"
                  :placeholder="formatPrice(calculateSellingPriceForEmptySeatsWithDifference(tier), city)" step="1" />
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="tl-empty-state">
          No tiers available for calculation.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPrice, formatNumber } from '../Helper/PriceDisplay.js';

import {
  calculateCost,
  calculateSellingPriceForTier,
  calculateEmptySeatCost,
  calculateSellingPriceForEmptySeatsWithDifference
} from '../Helper/Calculation.js';

export default {
  data() {
    return {
      tour: null,
      margin: 0,
      marginChild: 0,
      marginEmpty: 0,
      compticket: 0,
      factor: 1,
      tiers: [],
      userInputCostAdult: [],
      userInputSellingPriceAdult: [],
      userInputSellingPriceChild: [],
      userInputSellingPriceEmptySeat: []
    };
  },
  async created() {
    const tourId = this.$route.params.id;
    this.fetchTour(tourId);
  },
  computed: {
    city() {
      const cities = this.tour?.attributes?.cities?.data;
      if (!cities || cities.length === 0) return null;
      // Return 'Prague' if any city is Prague, otherwise the first city name
      const prague = cities.find(c => c.attributes.city === 'Prague');
      return prague ? 'Prague' : cities[0].attributes.city;
    }
  },
  methods: {
    formatPrice,
    formatNumber,
    calculateMargin(submittedPrice, buyingPrice) {
      if (buyingPrice === 0) return 0; // Avoid division by zero
      const numerator = 100 * (submittedPrice + 0.19 * buyingPrice) - 119 * buyingPrice;
      const denominator = submittedPrice + 0.19 * buyingPrice;
      return denominator === 0 ? 0 : (numerator / denominator).toFixed(2); // Return margin with 2 decimal precision
    },
    fetchTour(tourId) {
      this.$xhr.get(`/api/tours/${tourId}?populate=*`)
        .then(response => {
          this.tour = response.data.data;
          this.margin = this.tour.attributes.margin || 0;
          this.marginChild = this.tour.attributes.marginChild || 0;
          this.marginEmpty = this.tour.attributes.marginEmpty || 0;
          this.compticket = this.tour.attributes.compticket || 0;
          this.factor = this.tour.attributes.factor || 1;
          this.tiers = this.tour.attributes.tiers
            ? this.tour.attributes.tiers.map(tier => ({
              value: tier.value,
              userInputSellingPriceAdult: tier.userInputSellingPriceAdult,
              userInputSellingPriceChild: tier.userInputSellingPriceChild,
              userInputSellingPriceEmptySeat: tier.userInputSellingPriceEmptySeat
            }))
            : [];
          this.userInputSellingPriceAdult = this.tiers.map(tier => tier.userInputSellingPriceAdult);
          this.userInputSellingPriceChild = this.tiers.map(tier => tier.userInputSellingPriceChild);
          this.userInputSellingPriceEmptySeat = this.tiers.map(tier => tier.userInputSellingPriceEmptySeat);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    calculateCost(tier, type) {
      return calculateCost(this.tour, tier, this.compticket, this.factor, type);
    },
    calculateSellingPriceForTier(tier, type, index) {
      return calculateSellingPriceForTier(tier, this.tour, this.factor, this.margin, this.marginChild, index);
    },
    calculateEmptySeatCost(tier) {
      return calculateEmptySeatCost(this.tour, tier, this.factor);
    },
    calculateSellingPriceForEmptySeatsWithDifference(tier) {
      return calculateSellingPriceForEmptySeatsWithDifference(tier, this.tour, this.factor, this.marginEmpty, this.marginChild);
    },
    addTier() {
      this.tiers.push({
        value: 0,
        userInputSellingPriceAdult: '',
        userInputSellingPriceChild: '',
        userInputSellingPriceEmptySeat: ''
      });
      // Logic preserved
      this.userInputSellingPriceAdult;
      this.userInputSellingPriceChild;
      this.userInputSellingPriceEmptySeat;
    },
    removeTier(index) {
      this.tiers.splice(index, 1);
      this.userInputSellingPriceAdult.splice(index, 1);
      this.userInputSellingPriceChild.splice(index, 1);
      this.userInputSellingPriceEmptySeat.splice(index, 1);
    },
    clearTiers() {
      this.tiers = [];
      this.userInputSellingPriceAdult = [];
      this.userInputSellingPriceChild = [];
      this.userInputSellingPriceEmptySeat = [];
    },
    saveSettings() {
      // Update tiers array with user input values
      this.tiers = this.tiers.map((tier, index) => ({
        ...tier,
        userInputSellingPriceAdult: this.userInputSellingPriceAdult[index],
        userInputSellingPriceChild: this.userInputSellingPriceChild[index],
        userInputSellingPriceEmptySeat: this.userInputSellingPriceEmptySeat[index]
      }));

      const updatedData = {
        margin: this.margin,
        marginChild: this.marginChild,
        marginEmpty: this.marginEmpty,
        compticket: this.compticket,
        factor: this.factor,
        tiers: this.tiers
      };

      this.$xhr.put(`/api/tours/${this.tour.id}`, { data: updatedData })
        .then(() => {
          this.$bvToast.toast('Settings saved successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        })
        .catch(() => {
          this.$bvToast.toast('Failed to save settings.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          });
        });
    },
    updateTour() {
      const tourId = this.$route.params.id;
      this.fetchTour(tourId);
    }
  }
};
</script>

<style scoped>
/* ────────────── Page & General ────────────── */
.tl-page {
  padding: 16px 20px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  background: #f8fafc;
  min-height: 100vh;
}

.tl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 12px;
  color: #64748b;
}

.tl-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #E2923D;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ────────────── Section Headers ────────────── */
.tl-section-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 48px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
}

.tl-section-header--blue {
  border-top: 4px solid #3b82f6;
}

.tl-section-header--green {
  border-top: 4px solid #10b981;
}

.tl-section-header--orange {
  border-top: 4px solid #E2923D;
}

.tl-section-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
}

.tl-section-actions {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
}

/* ────────────── Cards ────────────── */
.tl-info-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
}

.tl-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 20px;
}

.tl-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tl-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ────────────── Tiers List ────────────── */
.tl-tiers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tl-tier-pill {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 4px 4px 4px 10px;
  gap: 8px;
  transition: all 0.2s;
}

.tl-tier-pill:focus-within {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, 0.1);
}

.tl-tier-index {
  font-size: 0.7rem;
  font-weight: 800;
  color: #94a3b8;
}

.tl-tier-input {
  width: 60px;
  border: none;
  background: transparent;
  font-weight: 700;
  color: #334155;
  outline: none;
  font-size: 0.9rem;
}

.tl-tier-remove {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.tl-tier-remove:hover {
  background: #fee2e2;
  color: #ef4444;
}

/* ────────────── Tables ────────────── */
.tl-table-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
}

.tl-table-scroll {
  overflow-x: auto;
}

.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.tl-th {
  background: #f9fafb;
  padding: 14px 18px;
  text-align: center;
  color: #64748b;
  font-weight: 700;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #f1f5f9;
}

.tl-th--name {
  text-align: left;
  border-right: 1px solid #f1f5f9;
  min-width: 180px;
}

.tl-td {
  padding: 12px 18px;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;
  color: #334155;
}

.tl-td--name {
  text-align: left;
  font-weight: 600;
  color: #1e293b;
  background: #fdfdfd;
  border-right: 1px solid #f1f5f9;
}

.tl-td--suggested {
  font-style: italic;
  color: #94a3b8;
  font-size: 0.8rem;
}

.tl-row:hover .tl-td {
  background: #f8fafc;
}

/* ────────────── Components ────────────── */
.tl-btn {
  height: 34px;
  padding: 0 16px;
  font-size: 0.82rem;
  font-weight: 700;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--ghost {
  background: #fff;
  border-color: #d1d5db;
  color: #475569;
}

.tl-btn--sm {
  height: 28px;
  padding: 0 10px;
  font-size: 0.75rem;
}

.tl-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tl-input {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #f8fafc;
  transition: all 0.2s;
  outline: none;
}

.tl-input:focus {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, 0.1);
}

.tl-input--sm {
  width: 90px;
}

.tl-input--full {
  width: 100%;
}

.tl-input--centered {
  text-align: center;
  font-weight: 700;
}

/* ────────────── Badges & Utils ────────────── */
.tl-calc-badges {
  display: flex;
  gap: 8px;
}

.tl-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.tl-badge--margin {
  background: #f0fdf4;
  color: #166534;
  border-color: #86efac;
}

.tl-price-highlight {
  font-weight: 800;
  color: #059669;
}

.tl-empty-state {
  padding: 32px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
}

.tl-empty-state--sm {
  padding: 12px;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  font-size: 0.8rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.pt-4 {
  padding-top: 1.5rem;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

.mb-3 {
  margin-bottom: 1rem;
}

.d-block {
  display: block;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.ml-3 {
  margin-left: 1rem;
}
</style>
